// Samples Gallery — the public sample applications, and importing one.
//
// The list comes from a catalog the backend fetches from a public repository
// (see backend/app/services/samples_service.py), so a new sample appears here
// without a platform release. Import sends the catalog ID, never a repository
// URL, and lands through the same git import as Workbench → Import from Git.
// Applications are grouped by the categories in data/sampleCategories.ts; the
// selected one is kept in the URL (?category=) so a link can open on it.

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  ArrowLeft, LayoutTemplate, RefreshCw, Github, Download, Check,
  AlertTriangle, Database,
} from 'lucide-react'
import toast from 'react-hot-toast'
import { fetchSamples, importSample, type SampleApp, type SampleCatalog } from '@/api/samples'
import { useProjectStore } from '@/store/projectStore'
import {
  SAMPLE_CATEGORIES, OTHER_CATEGORY, categoryOf, type SampleCategory,
} from '@/data/sampleCategories'

/** Object-type letters, spelled out for the counts strip on a card. */
const OBJECT_LABELS: Record<string, [string, string]> = {
  K: ['document repository', 'document repositories'],
  T: ['transaction', 'transactions'],
  V: ['visual', 'visuals'],
  D: ['dashboard', 'dashboards'],
  M: ['data model', 'data models'],
  A: ['business area', 'business areas'],
  G: ['process', 'processes'],
  R: ['report', 'reports'],
  W: ['approval workflow', 'approval workflows'],
  P: ['page', 'pages'],
}

function countsLine(objects?: Record<string, number>): string {
  if (!objects) return ''
  return Object.entries(objects)
    .filter(([, n]) => n > 0)
    .map(([k, n]) => {
      const label = OBJECT_LABELS[k]
      return `${n} ${label ? (n === 1 ? label[0] : label[1]) : k}`
    })
    .join(' · ')
}

export default function SamplesGalleryPage() {
  const navigate = useNavigate()
  const { fetchProjects, selectProject } = useProjectStore()

  const [catalog, setCatalog] = useState<SampleCatalog | null>(null)
  const [loading, setLoading] = useState(true)
  const [failed, setFailed] = useState<string | null>(null)
  const [importing, setImporting] = useState<string | null>(null)
  const [imported, setImported] = useState<Record<string, string>>({})

  const load = useCallback(async (refresh = false) => {
    setLoading(true)
    setFailed(null)
    try {
      const next = await fetchSamples(refresh)
      setCatalog(next)
      setImported(next.imported ?? {})
    } catch {
      setFailed('Could not load the samples catalog.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { void load() }, [load])

  const handleImport = async (app: SampleApp) => {
    setImporting(app.id)
    try {
      const result = await importSample(app.id)
      if (result.success && result.project_id) {
        setImported((m) => ({ ...m, [app.id]: result.project_id! }))
        toast.success(`${app.name} imported as "${result.project_id}"`)
        await fetchProjects()
      } else if (result.already_imported && result.project_id) {
        setImported((m) => ({ ...m, [app.id]: result.project_id! }))
        toast.error(result.error || `You already have ${app.name}`)
      } else {
        toast.error(result.error || `Could not import ${app.name}`)
      }
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })
        ?.response?.data?.detail || `Could not import ${app.name}`
      toast.error(msg)
    } finally {
      setImporting(null)
    }
  }

  const openProject = async (projectId: string) => {
    await selectProject(projectId)
    navigate('/workbench')
  }

  const apps = catalog?.applications ?? []

  const [searchParams, setSearchParams] = useSearchParams()
  const selected = searchParams.get('category') || 'all'
  const selectCategory = (key: string) => {
    const next = new URLSearchParams(searchParams)
    if (key === 'all') next.delete('category')
    else next.set('category', key)
    setSearchParams(next, { replace: true })
  }

  // Every category in its fixed order, each with the applications filed under
  // it, then "Others" for anything that names no known category.
  const groups = useMemo(() => {
    const byKey = new Map<string, SampleApp[]>()
    for (const app of apps) {
      const key = categoryOf(app.category).key
      byKey.set(key, [...(byKey.get(key) ?? []), app])
    }
    const list = SAMPLE_CATEGORIES.map((c) => ({ category: c, apps: byKey.get(c.key) ?? [] }))
    list.push({ category: OTHER_CATEGORY, apps: byKey.get(OTHER_CATEGORY.key) ?? [] })
    return list
  }, [apps])

  const shown = selected === 'all'
    ? groups.filter((g) => g.apps.length > 0)
    : groups.filter((g) => g.category.key === selected)

  const renderCard = (app: SampleApp) => (
    <Card
      key={app.id}
      app={app}
      importing={importing === app.id}
      importedAs={imported[app.id]}
      onImport={() => void handleImport(app)}
      onOpen={() => void openProject(imported[app.id])}
    />
  )

  return (
    <div style={{ maxWidth: 1100, margin: '0 auto', width: '100%' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.5rem' }}>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => navigate(-1)}
          style={{ display: 'flex', alignItems: 'center', gap: '0.375rem' }}
        >
          <ArrowLeft size={16} />
          Back
        </button>
        <h1 style={{ fontSize: '1.5625rem', fontWeight: 600, margin: 0 }}>Samples Gallery</h1>
        <button
          className="btn btn-ghost btn-sm"
          onClick={() => void load(true)}
          disabled={loading}
          title="Re-read the catalog"
          style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.375rem' }}
        >
          <RefreshCw size={15} style={loading ? { animation: 'spin 1s linear infinite' } : undefined} />
          Refresh
        </button>
      </div>

      <p style={{ color: 'var(--color-text-secondary)', marginTop: 0, marginBottom: '1.5rem' }}>
        Ready-made applications you can import and make your own. Each one is a
        public repository — importing clones it into a new project of yours.
      </p>

      {catalog?.stale && (
        <Banner tone="warn">
          The catalog could not be reached just now, so this is the last list we
          read. It may be out of date.
        </Banner>
      )}

      {loading && !catalog && <Skeleton />}

      {!loading && failed && (
        <Empty
          title="Samples are unavailable"
          body={failed}
          action={{ label: 'Try again', onClick: () => void load(true) }}
        />
      )}

      {!loading && !failed && apps.length === 0 && (
        <Empty
          title="No samples published yet"
          body="Nothing is listed in the catalog today. You can describe the app you want instead and Twasta will generate a first version."
          action={{ label: 'Create from a description', onClick: () => navigate('/builder') }}
        />
      )}

      {apps.length > 0 && (
        <>
          <CategoryBar
            groups={groups}
            total={apps.length}
            selected={selected}
            onSelect={selectCategory}
          />
          {shown.map(({ category, apps: inCategory }) => (
            <section key={category.key} style={{ marginBottom: '2rem' }}>
              <SectionHeading category={category} count={inCategory.length} />
              {inCategory.length > 0 ? (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
                    gap: '1rem',
                  }}
                >
                  {inCategory.map(renderCard)}
                </div>
              ) : (
                <Empty
                  title={`No ${category.label} samples yet`}
                  body="Nothing is published in this category today. You can describe the app you want instead and Twasta will generate a first version."
                  action={{ label: 'Create from a description', onClick: () => navigate('/generateapp') }}
                />
              )}
            </section>
          ))}
        </>
      )}
    </div>
  )
}

// ── pieces ──────────────────────────────────────────────────────────────────

function CategoryBar({ groups, total, selected, onSelect }: {
  groups: { category: SampleCategory; apps: SampleApp[] }[]
  total: number
  selected: string
  onSelect: (key: string) => void
}) {
  const chip = (key: string, label: string, count: number, icon?: string) => {
    const active = selected === key
    return (
      <button
        key={key}
        onClick={() => onSelect(key)}
        aria-pressed={active}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: '0.375rem',
          padding: '0.375rem 0.75rem', borderRadius: 999, cursor: 'pointer',
          fontSize: '0.8125rem', whiteSpace: 'nowrap',
          border: `1px solid ${active ? 'var(--color-primary)' : 'var(--color-border)'}`,
          background: active ? 'var(--color-primary)' : 'var(--color-bg-secondary)',
          color: active ? '#fff' : count ? 'var(--color-text)' : 'var(--color-text-secondary)',
        }}
      >
        {icon && <span className="material-icons" style={{ fontSize: 16 }}>{icon}</span>}
        {label}
        <span style={{ opacity: 0.75 }}>{count}</span>
      </button>
    )
  }
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '1.5rem' }}>
      {chip('all', 'All', total)}
      {groups.map(({ category, apps }) =>
        chip(category.key, category.label, apps.length, category.icon))}
    </div>
  )
}

function SectionHeading({ category, count }: { category: SampleCategory; count: number }) {
  return (
    <div style={{ marginBottom: '0.75rem' }}>
      <h2 style={{
        display: 'flex', alignItems: 'center', gap: '0.5rem',
        fontSize: '1.0625rem', fontWeight: 600, margin: 0,
      }}>
        <span className="material-icons" style={{ fontSize: 20, color: 'var(--color-primary)' }}>
          {category.icon}
        </span>
        {category.label}
        <span style={{ fontSize: '0.8125rem', fontWeight: 400, color: 'var(--color-text-secondary)' }}>
          {count}
        </span>
      </h2>
      <p style={{ margin: '0.25rem 0 0', fontSize: '0.8125rem', color: 'var(--color-text-secondary)' }}>
        {category.description}
      </p>
    </div>
  )
}

function Banner({ tone, children }: { tone: 'warn'; children: React.ReactNode }) {
  return (
    <div
      style={{
        display: 'flex', alignItems: 'flex-start', gap: '0.5rem',
        padding: '0.75rem 1rem', marginBottom: '1rem',
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderLeft: `3px solid ${tone === 'warn' ? '#f59e0b' : 'var(--color-border)'}`,
        borderRadius: 'var(--radius-md)',
        fontSize: '0.9375rem', color: 'var(--color-text-secondary)',
      }}
    >
      <AlertTriangle size={16} style={{ color: '#f59e0b', flexShrink: 0, marginTop: 2 }} />
      <span>{children}</span>
    </div>
  )
}

function Skeleton() {
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))', gap: '1rem' }}>
      {[0, 1, 2].map((i) => (
        <div
          key={i}
          style={{
            height: 220, background: 'var(--color-bg-secondary)',
            border: '1px solid var(--color-border)', borderRadius: 'var(--radius-lg)',
            opacity: 0.5,
          }}
        />
      ))}
    </div>
  )
}

function Empty({ title, body, action }: {
  title: string
  body: string
  action?: { label: string; onClick: () => void }
}) {
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        justifyContent: 'center', padding: '4rem 2rem', textAlign: 'center',
        background: 'var(--color-bg-secondary)',
        border: '1px dashed var(--color-border)',
        borderRadius: 'var(--radius-lg)',
      }}
    >
      <LayoutTemplate size={48} style={{ color: 'var(--color-text-secondary)', opacity: 0.5, marginBottom: '1rem' }} />
      <h2 style={{ fontSize: '1.1875rem', fontWeight: 600, marginBottom: '0.5rem' }}>{title}</h2>
      <p style={{ color: 'var(--color-text-secondary)', maxWidth: 480, marginBottom: action ? '1.5rem' : 0 }}>
        {body}
      </p>
      {action && (
        <button className="btn btn-primary" onClick={action.onClick}>{action.label}</button>
      )}
    </div>
  )
}

function Card({ app, importing, importedAs, onImport, onOpen }: {
  app: SampleApp
  importing: boolean
  importedAs?: string
  onImport: () => void
  onOpen: () => void
}) {
  const counts = countsLine(app.objects)
  return (
    <div
      style={{
        display: 'flex', flexDirection: 'column',
        background: 'var(--color-bg-secondary)',
        border: '1px solid var(--color-border)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
      }}
    >
      <div style={{ height: 4, background: app.theme_color || 'var(--color-primary)' }} />
      <div style={{ padding: '1.25rem', display: 'flex', flexDirection: 'column', flex: 1, gap: '0.75rem' }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', gap: '0.75rem' }}>
          <span
            className="material-icons"
            style={{
              fontSize: 29, color: app.theme_color || 'var(--color-primary)',
              flexShrink: 0, lineHeight: 1,
            }}
          >
            {app.icon || 'apps'}
          </span>
          <div style={{ minWidth: 0 }}>
            <h3 style={{ margin: 0, fontSize: '1.0625rem', fontWeight: 600 }}>{app.name}</h3>
            {app.category && (
              <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', marginTop: 2 }}>
                {categoryOf(app.category).key === OTHER_CATEGORY.key
                  && app.category.toLowerCase() !== OTHER_CATEGORY.key
                  ? app.category
                  : categoryOf(app.category).label}
              </div>
            )}
          </div>
        </div>

        <p
          style={{
            margin: 0, fontSize: '0.9375rem', color: 'var(--color-text-secondary)',
            lineHeight: 1.5, flex: 1,
          }}
        >
          {app.summary || app.description}
        </p>

        {counts && (
          <div style={{ fontSize: '0.8125rem', color: 'var(--color-text-secondary)', opacity: 0.85 }}>
            {counts}
          </div>
        )}

        {app.sample_data && (
          <div
            style={{
              display: 'inline-flex', alignItems: 'center', gap: '0.3rem',
              fontSize: '0.8125rem', color: 'var(--color-text-secondary)',
            }}
          >
            <Database size={13} />
            Includes demo data
          </div>
        )}

        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginTop: 'auto', paddingTop: '0.5rem' }}>
          {importedAs ? (
            <button className="btn btn-primary btn-sm" onClick={onOpen} title={`Open project "${importedAs}"`}>
              <Check size={14} style={{ marginRight: 4 }} />
              Open Application
            </button>
          ) : (
            <button className="btn btn-primary btn-sm" onClick={onImport} disabled={importing}>
              <Download size={14} style={{ marginRight: 4 }} />
              {importing ? 'Importing…' : 'Import'}
            </button>
          )}
          {app.repository && (
            <a
              href={app.repository}
              target="_blank"
              rel="noreferrer noopener"
              className="btn btn-ghost btn-sm"
              title="View the repository on GitHub"
              style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center' }}
            >
              <Github size={15} />
            </a>
          )}
        </div>
      </div>
    </div>
  )
}
